import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, setBearerToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, LogIn } from "lucide-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";

export default function Login() {
  const { login, authConfig, refresh } = useAuth();
  const { state } = useLocation();
  const error = state?.error;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (resp) => {
    try {
      const r = await api.post("/auth/google/verify", { credential: resp.credential });
      if (r.data?.session_token) setBearerToken(r.data.session_token);
      await refresh();
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connexion refusée.");
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data?.session_token) setBearerToken(r.data.session_token);
      await refresh();
      window.location.href = r.data?.user?.role === "collaborator" ? "/guide" : "/dashboard";
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Connexion refusée.");
    } finally {
      setBusy(false);
    }
  };

  const isGoogleMode = authConfig?.mode === "google" && !!authConfig?.google_client_id;

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-2"
      style={{ backgroundColor: "var(--bg-auth)" }}
    >
      <div className="hidden lg:flex relative items-end p-12 bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1615800098779-1be32e60cca3?crop=entropy&cs=srgb&fm=jpg&q=85)" }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(239,235,230,0.0) 0%, rgba(239,235,230,0.6) 100%)" }} />
        <div className="relative z-10 max-w-md">
          <div className="label-overline mb-3">FactureCab · v1</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold leading-tight text-stone-900">
            Toute votre facturation.<br/>Un seul endroit.
          </h1>
          <p className="mt-4 text-stone-700 text-sm leading-relaxed max-w-sm">
            Centralisation des factures EURL, patients, paiements et rapprochements bancaires — indépendamment de vos banques et prestataires.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm card-flat p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <ShieldCheck className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">FactureCab</div>
              <div className="text-xs text-stone-500">Outil privé · accès restreint</div>
            </div>
          </div>
          <h2 className="text-base font-semibold mb-1">Connexion</h2>
          <p className="text-sm text-stone-600 mb-5">
            Identifiez-vous avec votre compte Google (administrateur) ou vos identifiants fournis par l'administrateur.
          </p>
          {error ? (
            <div className="mb-4 p-3 text-sm rounded-md border" style={{ background: "#FEF2F2", color: "#991B1B", borderColor: "#FCA5A5" }} data-testid="login-error">
              {error}
            </div>
          ) : null}

          {isGoogleMode ? (
            <div data-testid="login-google-standard">
              <GoogleOAuthProvider clientId={authConfig.google_client_id}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error("Échec de la connexion Google.")}
                  useOneTap={false}
                  shape="rectangular"
                  theme="filled_blue"
                  text="signin_with"
                  width="320"
                />
              </GoogleOAuthProvider>
            </div>
          ) : (
            <Button data-testid="login-google-btn" onClick={login} className="w-full" style={{ background: "var(--brand)", color: "white" }}>
              Se connecter avec Google
            </Button>
          )}

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="text-[11px] uppercase tracking-widest text-stone-400">ou</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>

          <form onSubmit={handlePasswordLogin} className="space-y-3" data-testid="login-password-form">
            <div>
              <Label htmlFor="login-email" className="text-xs">E-mail</Label>
              <Input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom@exemple.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="login-password" className="text-xs">Mot de passe</Label>
              <Input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-password-submit"
              disabled={busy || !email || !password}
              variant="outline"
              className="w-full"
            >
              <LogIn className="w-4 h-4 mr-1" /> {busy ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
            Si votre compte n'est pas autorisé, contactez l'administrateur pour être ajouté.
          </p>
        </div>
      </div>
    </div>
  );
}
